import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DeleteOutline as DeleteIcon } from '@mui/icons-material';

import { Project } from '../../../models/Project';
import { SiteProjectService } from '../../../services/SiteProjectService';

export interface DeleteSiteDialogProps {
  /** 삭제 대상 사이트. null 이면 닫힘. */
  site: Project | null;
  open: boolean;
  onClose: () => void;
  /** 삭제 성공 시 상위에 알림. snackbar 메시지용 문구 전달. */
  onDeleted: (message: string) => void;
}

/**
 * 사이트 삭제 확인 모달 (deploy-service-selection 과 독립).
 *
 * 이름 타이핑 확인 UX — 사용자가 대상 사이트 이름을 정확히 입력해야 삭제
 * 버튼이 활성화된다. backend 는 soft delete 이므로 되돌릴 수 있지만 배포
 * 이력/레이어 매핑 데이터는 그대로 남는다는 경고 표시.
 */
export default function DeleteSiteDialog({
  site,
  open,
  onClose,
  onDeleted,
}: DeleteSiteDialogProps): React.ReactElement {
  const [inputName, setInputName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setInputName('');
      setError(null);
    }
  }, [open]);

  const expectedName = site?.name ?? '';
  const canSubmit = inputName === expectedName && !submitting && !!site;

  const handleConfirm = async (): Promise<void> => {
    if (!site || !canSubmit) return;
    const numericId = parseInt(site.id, 10);
    if (Number.isNaN(numericId)) {
      setError('사이트 ID 형식이 올바르지 않습니다.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await SiteProjectService.deleteProject(numericId);
      onDeleted(`현장 '${site.name}' 이(가) 삭제되었습니다`);
    } catch (e: unknown) {
      const err = e as { response?: { status?: number } } | undefined;
      if (err?.response?.status === 403) {
        setError('삭제 권한이 없습니다 (admin 역할 필요).');
      } else if (err?.response?.status === 404) {
        setError('이미 삭제된 현장일 수 있습니다.');
      } else {
        setError('삭제에 실패했습니다. 잠시 후 다시 시도해주세요.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = (): void => {
    if (submitting) return;
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="delete-site-dialog-title"
      data-testid="delete-site-dialog"
    >
      <DialogTitle id="delete-site-dialog-title">
        <Stack direction="row" alignItems="center" spacing={1}>
          <DeleteIcon color="error" />
          <span>현장 삭제</span>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <Alert severity="warning">
            이 작업은 soft delete 로 처리되어 관리자 문의 시 되돌릴 수 있지만,
            관련 <strong>배포 이력 / 레이어 매핑</strong>은 그대로 남습니다.
          </Alert>

          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              대상 현장
            </Typography>
            <Typography variant="h6" fontWeight={700}>
              {site?.name ?? '—'}
            </Typography>
            {site?.location && (
              <Typography variant="body2" color="text.secondary">
                {site.location}
              </Typography>
            )}
          </Box>

          <Box>
            <Typography variant="body2" gutterBottom>
              계속하려면 현장 이름(<strong>{expectedName}</strong>)을 정확히 입력하세요.
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={inputName}
              onChange={(e) => setInputName(e.target.value)}
              placeholder={expectedName}
              disabled={submitting}
              autoFocus
              inputProps={{
                'data-testid': 'delete-site-name-input',
                'aria-label': '삭제 확인용 현장 이름 입력',
              }}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          취소
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleConfirm}
          disabled={!canSubmit}
          startIcon={<DeleteIcon />}
          data-testid="delete-site-submit"
        >
          {submitting ? '삭제 중...' : '삭제'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
